import React, { useState, useCallback } from 'react';
import { View, ScrollView, StyleSheet, Alert } from 'react-native';
import {
  Card,
  Text,
  Button,
  TextInput,
  SegmentedButtons,
  Chip,
  Surface,
  useTheme,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch } from '../../store';
import { createJourney } from '../../store/slices/journeySlice';
import type {
  CreateJourneyInput,
  AssessmentResults,
  JourneyPreferences,
  SkillLevel,
  DifficultyPreference,
  ContentType,
  LearningStyle,
  JourneyNotificationFrequency,
  JourneyNotificationType,
} from '@coffeeplexity/shared';

import { logger } from '../../utils/logger';
import {
  SkillLevel as SkillLevelEnum,
  DifficultyPreference as DifficultyPreferenceEnum,
  ContentType as ContentTypeEnum,
  LearningStyle as LearningStyleEnum,
  JourneyNotificationFrequency as JourneyNotificationFrequencyEnum,
  JourneyNotificationType as JourneyNotificationTypeEnum,
} from '@coffeeplexity/shared';

interface CreateJourneyFormData {
  title: string;
  skillLevel: SkillLevel;
  experienceAreas: string[];
  preferredMethods: string[];
  availableEquipment: string[];
  timeAvailability: number;
  learningGoals: string[];
  focusAreas: string[];
  difficultyPreference: DifficultyPreference;
  contentTypes: ContentType[];
  learningStyle: LearningStyle;
  notificationEnabled: boolean;
  notificationFrequency: JourneyNotificationFrequency;
  notificationTypes: JourneyNotificationType[];
}

const SKILL_LEVELS: { value: SkillLevel; label: string }[] = [
  { value: SkillLevelEnum.BEGINNER, label: 'Beginner' },
  { value: SkillLevelEnum.INTERMEDIATE, label: 'Intermediate' },
  { value: SkillLevelEnum.ADVANCED, label: 'Advanced' },
];

const DIFFICULTY_LEVELS: { value: DifficultyPreference; label: string }[] = [
  { value: DifficultyPreferenceEnum.EASY, label: 'Easy' },
  { value: DifficultyPreferenceEnum.MODERATE, label: 'Moderate' },
  { value: DifficultyPreferenceEnum.CHALLENGING, label: 'Challenging' },
];

const CONTENT_TYPES: { value: ContentType; label: string }[] = [
  { value: ContentTypeEnum.VIDEO, label: 'Video' },
  { value: ContentTypeEnum.RECIPE, label: 'Recipes' },
  { value: ContentTypeEnum.READING, label: 'Reading' },
  { value: ContentTypeEnum.QUIZ, label: 'Quizzes' },
  { value: ContentTypeEnum.PRACTICE, label: 'Practice' },
];

const LEARNING_STYLES: { value: LearningStyle; label: string }[] = [
  { value: LearningStyleEnum.VISUAL, label: 'Visual' },
  { value: LearningStyleEnum.HANDS_ON, label: 'Hands-on' },
  { value: LearningStyleEnum.READING, label: 'Reading' },
  { value: LearningStyleEnum.MIXED, label: 'Mixed' },
];

const NOTIFICATION_FREQUENCIES: {
  value: JourneyNotificationFrequency;
  label: string;
}[] = [
  { value: JourneyNotificationFrequencyEnum.DAILY, label: 'Daily' },
  { value: JourneyNotificationFrequencyEnum.WEEKLY, label: 'Weekly' },
  { value: JourneyNotificationFrequencyEnum.CUSTOM, label: 'Custom' },
];

const EXPERIENCE_AREAS = [
  { label: 'Espresso', value: 'espresso' },
  { label: 'Pour Over', value: 'pour_over' },
  { label: 'French Press', value: 'french_press' },
  { label: 'AeroPress', value: 'aeropress' },
  { label: 'Cold Brew', value: 'cold_brew' },
  { label: 'Latte Art', value: 'latte_art' },
  { label: 'Cupping', value: 'cupping' },
  { label: 'Roasting', value: 'roasting' },
  { label: 'Grinding', value: 'grinding' },
  { label: 'Water Chemistry', value: 'water_chemistry' },
  { label: 'Brewing Theory', value: 'brewing_theory' },
  { label: 'Coffee Origins', value: 'coffee_origins' },
  { label: 'Processing Methods', value: 'processing_methods' },
];

const EQUIPMENT_OPTIONS = [
  'Espresso Machine',
  'Grinder',
  'Scale',
  'Kettle',
  'Pour Over Cone',
  'French Press',
  'AeroPress',
  'Moka Pot',
  'Chemex',
  'V60',
  'Kalita Wave',
];

export function CreateJourneyScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState<CreateJourneyFormData>({
    title: '',
    skillLevel: SkillLevelEnum.BEGINNER,
    experienceAreas: [],
    preferredMethods: [],
    availableEquipment: [],
    timeAvailability: 5,
    learningGoals: [],
    focusAreas: [],
    difficultyPreference: DifficultyPreferenceEnum.MODERATE,
    contentTypes: [ContentTypeEnum.VIDEO, ContentTypeEnum.RECIPE],
    learningStyle: LearningStyleEnum.MIXED,
    notificationEnabled: true,
    notificationFrequency: JourneyNotificationFrequencyEnum.DAILY,
    notificationTypes: [
      JourneyNotificationTypeEnum.ACTIVITY_REMINDER,
      JourneyNotificationTypeEnum.MILESTONE,
    ],
  });

  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [durationWeeks, setDurationWeeks] = useState(8);
  const totalSteps = 4;

  const updateFormData = useCallback(
    (updates: Partial<CreateJourneyFormData>) => {
      setFormData(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const toggleArrayItem = useCallback(
    (array: keyof CreateJourneyFormData, item: string) => {
      setFormData(prev => {
        const currentArray = prev[array] as string[];
        return {
          ...prev,
          [array]: currentArray.includes(item)
            ? currentArray.filter(i => i !== item)
            : [...currentArray, item],
        };
      });
    },
    []
  );

  const handleNext = useCallback(() => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, totalSteps]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim()) {
      Alert.alert('Error', 'Please enter a journey title');
      return;
    }

    if (formData.experienceAreas.length === 0) {
      Alert.alert('Error', 'Please select at least one experience area');
      return;
    }

    if (formData.availableEquipment.length === 0) {
      Alert.alert('Error', 'Please select at least one piece of equipment');
      return;
    }

    setLoading(true);

    try {
      const assessmentResults: AssessmentResults = {
        skill_level: formData.skillLevel,
        experience_areas: formData.experienceAreas,
        preferred_methods: formData.preferredMethods,
        available_equipment: formData.availableEquipment,
        time_commitment_hours_per_week: formData.timeAvailability,
        learning_goals: formData.learningGoals,
      };

      const preferences: JourneyPreferences = {
        duration_weeks: durationWeeks,
        focus_areas: formData.focusAreas,
        difficulty_preference: formData.difficultyPreference,
        content_types: formData.contentTypes,
        learning_style: formData.learningStyle,
        notification_preferences: {
          enabled: formData.notificationEnabled,
          frequency: formData.notificationFrequency,
          types: formData.notificationTypes,
        },
      };

      const journeyInput: CreateJourneyInput = {
        assessment_results: assessmentResults,
        preferences,
        goals: formData.learningGoals,
        ...(formData.title && { title: formData.title }), // Include user's custom title if provided
      };

      logger.log(
        'Creating journey with input:',
        JSON.stringify(journeyInput, null, 2)
      );

      const result = await dispatch(createJourney(journeyInput)).unwrap();

      logger.log('Journey created successfully:', result);

      // Stop loading before showing alert
      setLoading(false);

      // Navigate back with a flag to prevent immediate refetch
      (navigation as any).navigate('index', { skipRefresh: true });

      // Show success alert after navigation
      setTimeout(() => {
        Alert.alert(
          'Success!',
          "Your coffee journey has been created. Let's start learning!",
          [{ text: 'OK' }]
        );
      }, 300);
    } catch (error: any) {
      setLoading(false);
      Alert.alert(
        'Error',
        error.message || 'Failed to create journey. Please try again.'
      );
    }
  }, [formData, dispatch, navigation]);

  const renderStep1 = () => (
    <Card style={styles.card}>
      <Card.Content>
        <Text variant='headlineSmall' style={styles.stepTitle}>
          Basic Information
        </Text>
        <TextInput
          label='Journey Title'
          value={formData.title}
          onChangeText={text => updateFormData({ title: text })}
          style={styles.input}
          mode='outlined'
        />
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What's your current skill level?
        </Text>
        <SegmentedButtons
          value={formData.skillLevel}
          onValueChange={value =>
            updateFormData({ skillLevel: value as SkillLevel })
          }
          buttons={SKILL_LEVELS.map(level => ({
            value: level.value,
            label: level.label,
          }))}
          style={styles.segmentedButtons}
        />
        <Text variant='titleMedium' style={styles.sectionTitle}>
          How much time can you commit per week? (hours)
        </Text>
        <TextInput
          label='Hours per week'
          value={formData.timeAvailability.toString()}
          onChangeText={text => {
            const num = parseInt(text) || 0;
            if (num >= 0 && num <= 40) {
              updateFormData({ timeAvailability: num });
            }
          }}
          style={styles.input}
          mode='outlined'
          keyboardType='numeric'
        />
        <Text variant='titleMedium' style={styles.sectionTitle}>
          How many weeks for your journey?
        </Text>
        <TextInput
          label='Duration (weeks)'
          value={durationWeeks.toString()}
          onChangeText={text => {
            const num = parseInt(text) || 0;
            if (num >= 1 && num <= 52) {
              setDurationWeeks(num);
            }
          }}
          style={styles.input}
          mode='outlined'
          keyboardType='numeric'
        />
      </Card.Content>
    </Card>
  );

  const renderStep2 = () => (
    <Card style={styles.card}>
      <Card.Content>
        <Text variant='headlineSmall' style={styles.stepTitle}>
          Experience & Equipment
        </Text>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What brewing methods have you tried?
        </Text>
        <View style={styles.chipContainer}>
          {EXPERIENCE_AREAS.map(area => (
            <Chip
              key={area.value}
              selected={formData.experienceAreas.includes(area.value)}
              onPress={() => toggleArrayItem('experienceAreas', area.value)}
              style={styles.chip}
              mode='outlined'
            >
              {area.label}
            </Chip>
          ))}
        </View>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What equipment do you have?
        </Text>
        <View style={styles.chipContainer}>
          {EQUIPMENT_OPTIONS.map(equipment => (
            <Chip
              key={equipment}
              selected={formData.availableEquipment.includes(equipment)}
              onPress={() => toggleArrayItem('availableEquipment', equipment)}
              style={styles.chip}
              mode='outlined'
            >
              {equipment}
            </Chip>
          ))}
        </View>
      </Card.Content>
    </Card>
  );

  const renderStep3 = () => (
    <Card style={styles.card}>
      <Card.Content>
        <Text variant='headlineSmall' style={styles.stepTitle}>
          Learning Preferences
        </Text>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What would you like to focus on?
        </Text>
        <View style={styles.chipContainer}>
          {EXPERIENCE_AREAS.map(area => (
            <Chip
              key={area.value}
              selected={formData.focusAreas.includes(area.value)}
              onPress={() => toggleArrayItem('focusAreas', area.value)}
              style={styles.chip}
              mode='outlined'
            >
              {area.label}
            </Chip>
          ))}
        </View>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Preferred difficulty level
        </Text>
        <SegmentedButtons
          value={formData.difficultyPreference}
          onValueChange={value =>
            updateFormData({
              difficultyPreference: value as DifficultyPreference,
            })
          }
          buttons={DIFFICULTY_LEVELS.map(level => ({
            value: level.value,
            label: level.label,
          }))}
          style={styles.segmentedButtons}
        />
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What content types do you prefer?
        </Text>
        <View style={styles.chipContainer}>
          {CONTENT_TYPES.map(type => (
            <Chip
              key={type.value}
              selected={formData.contentTypes.includes(type.value)}
              onPress={() => toggleArrayItem('contentTypes', type.value)}
              style={styles.chip}
              mode='outlined'
            >
              {type.label}
            </Chip>
          ))}
        </View>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Learning style
        </Text>
        <SegmentedButtons
          value={formData.learningStyle}
          onValueChange={value =>
            updateFormData({ learningStyle: value as LearningStyle })
          }
          buttons={LEARNING_STYLES.map(style => ({
            value: style.value,
            label: style.label,
          }))}
          style={styles.segmentedButtons}
        />
      </Card.Content>
    </Card>
  );

  const renderStep4 = () => (
    <Card style={styles.card}>
      <Card.Content>
        <Text variant='headlineSmall' style={styles.stepTitle}>
          Goals & Notifications
        </Text>
        <Text variant='titleMedium' style={styles.sectionTitle}>
          What are your learning goals?
        </Text>
        <TextInput
          label='Learning goals (comma separated)'
          value={formData.learningGoals.join(', ')}
          onChangeText={text => {
            const goals = text
              .split(',')
              .map(g => g.trim())
              .filter(g => g.length > 0);
            updateFormData({ learningGoals: goals });
          }}
          style={styles.input}
          mode='outlined'
          multiline
          numberOfLines={3}
        />
        <Text variant='titleMedium' style={styles.sectionTitle}>
          Notification preferences
        </Text>
        <SegmentedButtons
          value={formData.notificationFrequency}
          onValueChange={value =>
            updateFormData({
              notificationFrequency: value as JourneyNotificationFrequency,
            })
          }
          buttons={NOTIFICATION_FREQUENCIES.map(freq => ({
            value: freq.value,
            label: freq.label,
          }))}
          style={styles.segmentedButtons}
        />
      </Card.Content>
    </Card>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <View
      style={[
        styles.container,
        { paddingTop: insets.top, backgroundColor: theme.colors.background },
      ]}
    >
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Progress indicator */}
        <Surface style={styles.progressContainer}>
          <Text variant='titleMedium' style={styles.progressText}>
            Step {currentStep} of {totalSteps}
          </Text>
          <View style={styles.progressBar}>
            <View
              style={[
                styles.progressFill,
                { width: `${(currentStep / totalSteps) * 100}%` },
              ]}
            />
          </View>
        </Surface>

        {renderCurrentStep()}

        {/* Navigation buttons */}
        <View style={styles.buttonContainer}>
          {currentStep > 1 && (
            <Button
              mode='outlined'
              onPress={handlePrevious}
              style={[styles.button, styles.previousButton]}
              disabled={loading}
            >
              Previous
            </Button>
          )}

          {currentStep < totalSteps ? (
            <Button
              mode='contained'
              onPress={handleNext}
              style={[styles.button, styles.nextButton]}
              disabled={loading}
            >
              Next
            </Button>
          ) : (
            <Button
              mode='contained'
              onPress={handleSubmit}
              style={[styles.button, styles.submitButton]}
              disabled={loading}
              loading={loading}
            >
              Create Journey
            </Button>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  progressContainer: {
    margin: 16,
    padding: 16,
    borderRadius: 8,
    elevation: 2,
  },
  progressText: {
    textAlign: 'center',
    marginBottom: 12,
  },
  progressBar: {
    height: 4,
    backgroundColor: '#e0e0e0',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#6200ee',
    borderRadius: 2,
  },
  card: {
    margin: 16,
    marginTop: 8,
  },
  stepTitle: {
    marginBottom: 20,
    textAlign: 'center',
  },
  sectionTitle: {
    marginTop: 20,
    marginBottom: 12,
    fontWeight: '600',
  },
  input: {
    marginBottom: 16,
  },
  segmentedButtons: {
    marginBottom: 16,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  chip: {
    margin: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16,
    paddingBottom: 32,
  },
  button: {
    flex: 1,
    marginHorizontal: 8,
  },
  previousButton: {
    marginRight: 8,
  },
  nextButton: {
    marginLeft: 8,
  },
  submitButton: {
    marginLeft: 8,
  },
});
